import { useMemo } from 'react';

/**
 * Custom hook to manage floorplan filtering logic
 */
export function useFloorplanFilters(combinedFloorplans, filters) {
    // Get unique locations from combined data
    const availableLocations = useMemo(() => {
        const locations = new Set();
        combinedFloorplans.forEach(plan => {
            if (plan.location) {
                locations.add(plan.location);
            }
        });
        return Array.from(locations).sort();
    }, [combinedFloorplans]);

    // Get unique home types
    const availableHomeTypes = useMemo(() => {
        const types = new Set();
        combinedFloorplans.forEach(plan => {
            const homeType = plan['Home Type'] || plan.HomeType || plan.homeType;
            if (homeType) {
                types.add(homeType);
            }
        });
        return Array.from(types).sort();
    }, [combinedFloorplans]);

    // Get unique bed counts
    const availableBeds = useMemo(() => {
        const beds = new Set();
        combinedFloorplans.forEach(plan => {
            const bedCount = plan.Beds || plan.beds;
            if (bedCount) {
                beds.add(bedCount);
            }
        });
        return Array.from(beds).sort((a, b) => a - b);
    }, [combinedFloorplans]);

    // Transform floorplan data to component format
    const transformedFloorplans = useMemo(() => {
        if (!combinedFloorplans || combinedFloorplans.length === 0) return [];
        
        return combinedFloorplans.map((plan, index) => {
            const name = plan.Name || plan.name;
            const beds = plan.Beds || plan.beds;
            const baths = plan.Baths || plan.baths;
            const garage = plan.Garage || plan.garage;
            const sqft = plan.SF || plan.sf;
            const stories = plan.Story || plan.story || plan.stories;
            const homeType = plan['Home Type'] || plan.HomeType || plan.homeType;
            const thumb = plan.Thumb || plan.thumb;
            
            return {
                id: plan.Id || plan.id || index,
                image: thumb || `/images/floorplans/${name?.toLowerCase().replace(/\s+/g, '-')}.png`,
                homeType: homeType || 'Standard',
                planName: name,
                beds: beds ? `${beds}+ Beds` : 'N/A',
                baths: baths ? `${baths}+ Baths` : 'N/A',
                garage: garage ? `${garage}+ Car Garage` : '2+ Car Garage',
                sqft: sqft ? `${sqft.toLocaleString()}+ SF` : 'N/A',
                stories: stories ? `${stories}+ Story` : '1+ Story',
                location: plan.location || '',
                community: plan.community || '',
                originalData: plan
            };
        });
    }, [combinedFloorplans]);

    // Filter and deduplicate floorplans
    const filteredFloorplans = useMemo(() => {
        const filtered = transformedFloorplans.filter(plan => {
            if (filters.location && plan.location !== filters.location) return false;
            if (filters.homeType && plan.homeType !== filters.homeType) return false;
            
            if (filters.beds) {
                const minBeds = parseInt(filters.beds);
                const planBeds = parseInt(plan.beds);
                if (isNaN(planBeds) || planBeds < minBeds) return false;
            }
            
            return true;
        });

        // Always deduplicate by floorplan name
        const uniquePlans = new Map();
        filtered.forEach(plan => {
            if (!uniquePlans.has(plan.planName)) {
                uniquePlans.set(plan.planName, plan);
            }
        });
        return Array.from(uniquePlans.values());
    }, [transformedFloorplans, filters]);

    return {
        availableLocations,
        availableHomeTypes,
        availableBeds,
        filteredFloorplans
    };
}
